/**
 * Content filter bar
 *
 * Almost nothing here is behaviour. Every filter in sections/content-filter-bar.liquid
 * is a link that rewrites the `/blogs/<handle>/tagged/<tags>` path, so the bar
 * filters, marks its own active state and stays keyboard operable with this
 * file absent.
 *
 * What is left needs a custom element rather than Alpine, because it is URL
 * work rather than ephemeral UI state:
 *
 * - Opening the mobile drawer. It is a modal <dialog>, so showModal() is the
 *   one line that buys the focus trap and Escape-to-close. Closing needs no
 *   script at all — the close button is a <form method="dialog"> submit.
 * - Keeping the drawer's footer link in step with its topic checkboxes. A GET
 *   form cannot produce a path segment, so the footer is an anchor whose href
 *   is rebuilt here on every change. It ships pointing at the currently
 *   applied filter, which is exactly what the checkboxes show, so it is
 *   already correct before this runs.
 * - Filling each content-type tab's article count. The number is paginate.items
 *   on that tag's URL, read from the article-tag-count section so the blog
 *   page's own pagination is left alone.
 */

if (!customElements.get('content-filter-bar')) {
  class ContentFilterBar extends HTMLElement {
    abortController = undefined;

    constructor() {
      super();

      this.onOpenClick = this.onOpenClick.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onDrawerClose = this.onDrawerClose.bind(this);
      this.onDrawerChange = this.onDrawerChange.bind(this);
    }

    connectedCallback() {
      this.preserveQuery();
      this.loadCounts();

      if (!this.drawer) {
        return;
      }

      this.openButton?.addEventListener('click', this.onOpenClick);
      this.drawer.addEventListener('click', this.onDrawerClick);
      this.drawer.addEventListener('close', this.onDrawerClose);
      this.drawer.addEventListener('change', this.onDrawerChange);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.openButton?.removeEventListener('click', this.onOpenClick);
      this.drawer?.removeEventListener('click', this.onDrawerClick);
      this.drawer?.removeEventListener('close', this.onDrawerClose);
      this.drawer?.removeEventListener('change', this.onDrawerChange);
    }

    /**
     * Filter links are rendered with only ?view=<suffix>. Every other
     * parameter in the address bar is added here, without overriding one the
     * link already sets.
     * @returns {void}
     */
    preserveQuery() {
      const current = new URLSearchParams(window.location.search);
      current.delete('page');
      // The links carry the blog's view from the server; the address bar's view
      // is this page's (?view=podcasts on /pages/podcasts) and the blog lacks it.
      current.delete('view');

      const base = this.dataset.contentFilterBase;

      if (!base || [...current.keys()].length === 0) {
        return;
      }

      for (const link of this.querySelectorAll('a[href]')) {
        const url = new URL(link.href, window.location.origin);

        if (url.origin !== window.location.origin) {
          continue;
        }

        if (url.pathname !== base && !url.pathname.startsWith(`${base}/tagged/`)) {
          continue;
        }

        for (const [key, value] of current) {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }

        link.href = `${url.pathname}${url.search}${url.hash}`;
      }
    }

    /**
     * Each slot names the tagged blog URL whose article total it should show.
     * @returns {Promise<void>}
     */
    async loadCounts() {
      const slots = [...this.querySelectorAll('[data-filter-count-url]')];

      if (slots.length === 0) {
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      await Promise.all(slots.map((slot) => this.loadCount(slot)));
    }

    /**
     * @param {HTMLElement} slot - Empty count node with data-filter-count-url
     * @returns {Promise<void>}
     */
    async loadCount(slot) {
      const count = await this.fetchCount(slot.dataset.filterCountUrl);

      if (count === null) {
        return;
      }

      this.renderCount(slot, count);
    }

    /**
     * @param {string} url - Tagged blog URL
     * @returns {Promise<number | null>}
     */
    async fetchCount(url) {
      try {
        const params = new URLSearchParams({section_id: 'article-tag-count'});
        // On a preview link (?preview_theme_id= in the address) the request must
        // name the previewed theme, or the live theme answers it — and has no
        // article-tag-count section. Not on `shopify theme dev`: it serves the
        // development theme itself and redirects a request that names one.
        const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewThemeId) {
          params.set('preview_theme_id', previewThemeId);
        }

        const response = await fetch(`${url}?${params}`, {
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          return null;
        }

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const raw = html.querySelector('[data-tagged-article-count]')?.dataset.taggedArticleCount;
        const count = Number(raw);

        if (raw === undefined || !Number.isFinite(count)) {
          return null;
        }

        return count;
      } catch {
        // A count that cannot be fetched stays hidden rather than guessed.
        return null;
      }
    }

    /**
     * @param {HTMLElement} slot
     * @param {number} count
     */
    renderCount(slot, count) {
      slot.textContent = count.toLocaleString();
      slot.classList.remove('content-filter-chip__count--pending');
    }

    /** @returns {HTMLDialogElement | null} */
    get drawer() {
      return this.querySelector('[data-content-filter-drawer]');
    }

    /** @returns {HTMLButtonElement | null} */
    get openButton() {
      return this.querySelector('[data-content-filter-open]');
    }

    /** @returns {HTMLAnchorElement | null} */
    get applyLink() {
      return this.querySelector('[data-content-filter-apply]');
    }

    /** @returns {NodeListOf<HTMLInputElement>} */
    get topicInputs() {
      return this.querySelectorAll('[data-content-filter-topic]');
    }

    onOpenClick() {
      this.drawer?.showModal();
    }

    /**
     * Light dismiss. showModal() puts the dialog in the top layer, so a click
     * that lands on the dialog element itself landed on the backdrop.
     * @param {MouseEvent} event
     */
    onDrawerClick(event) {
      if (event.target === this.drawer) {
        this.drawer.close();
      }
    }

    /* Browsers return focus to the opener on close, but not in every engine
       when the close came from a form submit rather than Escape. */
    onDrawerClose() {
      this.openButton?.focus();
    }

    /** @param {Event} event */
    onDrawerChange(event) {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }

      if (!event.target.matches('[data-content-filter-topic]')) {
        return;
      }

      this.syncApplyUrl();
    }

    /**
     * Rebuilds the footer link from the checkbox state, preserving the filters
     * the drawer does not control — the active content type, and any tag
     * reached by URL that no block declares.
     * @returns {void}
     */
    syncApplyUrl() {
      const link = this.applyLink;
      if (!link) {
        return;
      }

      const base = this.dataset.contentFilterBase || '';
      const kept = (this.dataset.contentFilterKept || '').split('+').filter(Boolean);
      const selected = [];

      for (const input of this.topicInputs) {
        if (input.checked) {
          selected.push(input.value);
        }
      }

      /* A repeated tag makes the whole path return nothing — /tagged/recipes
         has articles, /tagged/recipes+recipes has none — so the list is
         de-duplicated before it is joined. */
      const handles = [...new Set([...kept, ...selected])];
      const path = handles.length > 0 ? `${base}/tagged/${handles.join('+')}` : base;
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      // The blog's view, not this page's: on /pages/podcasts?view=podcasts the
      // address bar's view would send the blog to a template it doesn't have.
      params.delete('view');
      if (this.dataset.contentFilterView) {
        params.set('view', this.dataset.contentFilterView);
      }

      const query = params.toString();

      link.href = query ? `${path}?${query}` : path;
    }
  }

  customElements.define('content-filter-bar', ContentFilterBar);
}
